import { Alert, alertClasses, AlertTitle, alertTitleClasses, Box, type SxProps, type Theme, Typography } from '@mui/material'
import React, { type FC, type ReactNode } from 'react'

import { type BannerAction, type BannerSeverity, NotificationSeverity, Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { Button, IconButton } from '../Button'
import { CloseIcon } from '../../theme/icons'
import { notificationIconMapping, toAlertColor } from './Severity'

export type { BannerAction, BannerSeverity }

export type BannerProps = Readonly<{
    severity: BannerSeverity
    title: ReactNode
    description?: string
    action?: BannerAction
    /** Omit for a banner that can't be dismissed */
    onClose?: () => void
    /** `false` for a banner whose content keeps changing (e.g. a countdown), so screen readers don't re-announce it on every change */
    live?: boolean
    'data-testid'?: string
}>

const actionsStyle: SxProps<Theme> = { alignItems: 'center', display: 'flex', gap: 1 }

const actionButtonVariant: Readonly<Record<BannerSeverity, 'dangerOutline' | 'primaryOutline'>> = {
    [NotificationSeverity.ERROR]: 'dangerOutline',
    [NotificationSeverity.WARNING]: 'dangerOutline',
    [NotificationSeverity.INFO]: 'primaryOutline',
}

const closeButtonVariant: Readonly<Record<BannerSeverity, 'dangerGhost' | 'primaryGhost'>> = {
    [NotificationSeverity.ERROR]: 'dangerGhost',
    [NotificationSeverity.WARNING]: 'dangerGhost',
    [NotificationSeverity.INFO]: 'primaryGhost',
}

/**
 * Full-width by design (docked below the top nav, pushes page content down) - actual placement
 * in the page layout is the caller's responsibility, not this component's.
 * Overrides the `notification` variant's colors and border: Banner has a stronger fill and no border, unlike Toast/InlineAlert.
 */
const createBannerStyle = (severity: BannerSeverity): SxProps<Theme> => (theme) => {
    const ramp = theme.palette[toAlertColor[severity]]
    return {
        width: '100%',
        border: 'none',
        alignItems: 'center',
        backgroundColor: ramp[100],
        color: ramp[700],
        [`& .${alertClasses.icon}`]: { color: ramp[700] },
        [`& .${alertTitleClasses.root}`]: { color: ramp[800] },
    }
}

export const Banner: FC<BannerProps> = ({ severity, title, description, action, onClose, live = true, 'data-testid': dataTestId = 'notification-banner' }) => {
    const translator = useTranslator()

    return (
        <Alert
            data-testid={dataTestId}
            data-severity={severity}
            role={live ? 'alert' : 'none'}
            severity={toAlertColor[severity]}
            iconMapping={notificationIconMapping}
            variant="notification"
            sx={createBannerStyle(severity)}
            action={(action ?? onClose) && (
                <Box sx={actionsStyle}>
                    {action && (
                        <Button data-testid="notification-banner-action" variant={actionButtonVariant[severity]} size="large" onClick={action.onClick}>
                            {action.label}
                        </Button>
                    )}
                    {onClose && (
                        <IconButton
                            data-testid="notification-banner-close"
                            aria-label={translator.formatTranslation(Translation.CLOSE)}
                            variant={closeButtonVariant[severity]}
                            size="small"
                            onClick={onClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            )}
        >
            <AlertTitle gutterBottom={Boolean(description)}>{title}</AlertTitle>
            {description && <Typography variant="body2">{description}</Typography>}
        </Alert>
    )
}
